import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { CAPABILITIES, CREDIBILITY } from "@/data/content";
import { MaskedLines, Reveal, SectionHeader, EASE } from "@/components/motion";
import { GeoArt } from "@/components/GeoArt";
import { Marquee } from "@/components/Marquee";
import { Divisions } from "@/components/Divisions";
import { CTABand } from "@/components/CTABand";
import { usePageMeta } from "@/lib/seo";

const GOV_AREAS = [
  "Professional & Business Services",
  "Supplies & Procurement",
  "Training & Development",
  "Logistics & Support Services",
];

const Hero = () => (
  <section
    className="grid-lines-light relative overflow-hidden pt-36 lg:pt-44"
    data-testid="hero-section"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          >
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs font-medium uppercase tracking-[0.3em] text-charcoal/60">
              Enterprise &amp; Business Solutions
            </span>
          </motion.div>

          <MaskedLines
            testId="hero-headline"
            className="mt-8 font-heading text-4xl font-extrabold leading-[1.04] tracking-tight text-navy sm:text-5xl lg:text-[4.25rem]"
            delay={0.2}
            lines={[
              "Business Solutions",
              <>
                Built for <span className="text-gold">What&rsquo;s Next.</span>
              </>,
            ]}
          />

          <Reveal delay={0.55}>
            <p
              className="mt-8 max-w-xl leading-relaxed text-charcoal/70"
              data-testid="hero-subcopy"
            >
              Iconicho, LLC provides enterprise and business solutions across
              consulting, professional services, marketing, training,
              procurement, logistics, and operational support. We bring
              practical expertise and adaptable solutions to businesses,
              organizations, and government partners.
            </p>
          </Reveal>

          <Reveal delay={0.7}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/#capabilities"
                data-testid="hero-cta-capabilities"
                className="group inline-flex items-center gap-3 rounded-sm bg-navy px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-charcoal"
              >
                Explore Our Capabilities
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-3 rounded-sm border border-navy px-8 py-4 text-sm font-medium tracking-wide text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
              >
                Work With Iconicho
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.85}>
            <div className="mt-16 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-charcoal/10 pt-6 text-xs font-medium uppercase tracking-[0.2em] text-charcoal/45">
              <span>Commercial</span>
              <span className="h-1 w-1 rotate-45 bg-gold" />
              <span>Government</span>
              <span className="h-1 w-1 rotate-45 bg-gold" />
              <span>Consulting to Logistics</span>
            </div>
          </Reveal>
        </div>

        <div className="hidden lg:col-span-5 lg:block">
          <GeoArt />
        </div>
      </div>
    </div>
    <div className="mx-auto mt-16 max-w-7xl px-6 pb-10 lg:px-10 lg:mt-24">
      <motion.div
        className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.3em] text-charcoal/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <motion.span
          className="block h-8 w-px bg-gold"
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
        Scroll
      </motion.div>
    </div>
  </section>
);

const Intro = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="intro-section">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeader
          index="01"
          overline="The Company"
          title="One Company. Multiple Solutions."
        />
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal delay={0.1}>
          <p className="text-lg leading-relaxed text-charcoal/75">
            Iconicho operates across multiple business solution categories —
            consulting, professional services, training, marketing,
            administrative support, procurement, supply, and logistics — and
            adapts its capabilities to meet the requirements of each
            organization and engagement.
          </p>
          <p className="mt-6 leading-relaxed text-charcoal/70">
            Whether supporting a growing business or responding to a defined
            contract scope, Iconicho brings practical expertise, operational
            structure, and flexible solutions built for execution.
          </p>
          <Link
            to="/about"
            data-testid="intro-about-link"
            className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-navy"
          >
            <span className="border-b border-gold/50 pb-0.5 transition-colors duration-300 group-hover:border-gold">
              More about Iconicho
            </span>
            <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Capabilities = () => (
  <section
    id="capabilities"
    className="border-t border-charcoal/10 scroll-mt-24"
    data-testid="capabilities-section"
  >
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <SectionHeader index="02" overline="What We Do" title="Our Capabilities" />
      <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-charcoal/10 bg-charcoal/10 sm:grid-cols-2 lg:grid-cols-4">
        {CAPABILITIES.map((cap, i) => (
          <Reveal key={cap.title} delay={0.05 * i} className="h-full">
            <div
              data-testid={`capability-card-${cap.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group flex h-full flex-col bg-white p-8 transition-colors duration-300 hover:bg-paper"
            >
              <div className="flex items-center justify-between">
                <cap.icon
                  className="h-6 w-6 text-navy transition-colors duration-300 group-hover:text-gold"
                  strokeWidth={1.5}
                />
                <span className="font-heading text-xs font-bold tracking-[0.25em] text-charcoal/25">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-8 font-heading text-lg font-bold tracking-tight text-navy">
                {cap.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/65">
                {cap.description}
              </p>
              <span className="mt-auto block pt-8">
                <span className="block h-px w-8 bg-gold/40 transition-[width] duration-500 group-hover:w-16" />
              </span>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.35} className="h-full">
          <Link
            to="/business-solutions"
            data-testid="capabilities-cta-card"
            className="group flex h-full flex-col justify-between bg-navy p-8 text-white transition-colors duration-300 hover:bg-charcoal"
          >
            <span className="font-heading text-lg font-bold leading-snug tracking-tight">
              Explore the full scope of our business solutions.
            </span>
            <span className="mt-10 inline-flex items-center gap-2 text-sm font-medium">
              Business Solutions
              <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const GovBand = () => (
  <section className="noise-overlay bg-navy" data-testid="gov-band-section">
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHeader
            dark
            index="03"
            overline="Public Sector"
            title="Government Contracting"
          />
          <Reveal delay={0.12}>
            <p className="mt-8 font-heading text-xl font-light leading-relaxed text-white/85 sm:text-2xl text-balance">
              Flexible capabilities. Reliable execution. Business solutions
              designed to meet a range of organizational and procurement needs.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl leading-relaxed text-white/60">
              Iconicho is positioned to pursue opportunities across professional
              services, business support, training, marketing, administrative
              services, procurement, supplies, logistics, and other applicable
              contract opportunities.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <Link
              to="/government-contracting"
              data-testid="gov-band-cta-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-sm bg-gold px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-gold-dark"
            >
              View Contracting Capabilities
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <div className="flex h-full flex-col justify-center">
            {GOV_AREAS.map((area, i) => (
              <Reveal key={area} delay={0.1 + i * 0.08}>
                <div className="flex items-center gap-5 border-b border-white/10 py-5 first:border-t">
                  <span className="font-heading text-xs font-bold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm font-medium tracking-wide text-white/80">
                    {area}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Credibility = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="credibility-section">
    <SectionHeader
      index="05"
      overline="Why Iconicho"
      title="A Business Built for Opportunity"
    />
    <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
      {CREDIBILITY.map((item, i) => (
        <Reveal key={item.title} delay={0.08 * i}>
          <div
            data-testid={`credibility-item-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="border-t border-charcoal/15 pt-6"
          >
            <span className="font-heading text-xs font-bold tracking-[0.25em] text-gold">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-navy">
              {item.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-charcoal/65">
              {item.text}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  usePageMeta(
    "Iconicho, LLC — Enterprise & Business Solutions",
    "Iconicho, LLC is an enterprise and business solutions company providing business consulting, professional services, leadership training, marketing, administrative support, procurement, supply, logistics, and government contracting solutions."
  );
  return (
    <>
      <Hero />
      <Marquee />
      <Intro />
      <Capabilities />
      <GovBand />
      <Divisions index="04" />
      <Credibility />
      <CTABand testId="home-cta-band" />
    </>
  );
}
